import { useAPI } from "@humansignal/core";
import { Button, SettingsCard, Toggle, useToast } from "@humansignal/ui";
import { confirm } from "@afriannotate/app/components/Modal/Modal";
import { Loading } from "@afriannotate/app/components/Loading/Loading";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import "../reuse/Datasets.prefix.css";
import "../training/Training.prefix.css";

const when = (iso) => (iso ? format(new Date(iso), "d MMM yyyy, HH:mm") : "");

/** Platform, Settings, Business: the one switch that starts the business. */
export const BusinessTab = ({ canEdit }) => {
  const api = useAPI();
  const toast = useToast();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState(null);
  const saveNames = async () => {
    setBusy(true);
    const res = await api.callApi("setBusiness", { body: { names }, errorFilter: () => true });
    setBusy(false);
    if (!res?.floor) return toast?.show({ message: "The names could not be saved.", type: "error" });
    setState(res);
    setNames(null);
    toast?.show({ message: "Names saved." });
  };
  const load = useCallback(
    () => api.callApi("business", { errorFilter: () => true }).then((res) => setState(res?.floor ? res : false)),
    [api],
  );
  useEffect(() => {
    load();
  }, [load]);

  const flip = (on) =>
    confirm({
      title: on ? "Start the business?" : "Stop showing the business?",
      body: on
        ? "Organisations will see subscription types, prices, invoices, compute prices and allowances; every organisation is served as its own type instead of the top one. Publish the docs afterwards so the money pages appear."
        : "Everything shown to organisations goes back to neutral: no prices, subscriptions, invoices or allowances, and every organisation is served as the top type. Publish the docs afterwards so the money pages disappear.",
      okText: on ? "Start" : "Stop",
      buttonLook: on ? "primary" : "negative",
      onOk: async () => {
        setBusy(true);
        const res = await api.callApi("setBusiness", { body: { on }, errorFilter: () => true });
        setBusy(false);
        if (!res?.floor) return toast?.show({ message: "It could not be switched.", type: "error" });
        setState(res);
        toast?.show({ message: on ? "The business is on." : "Everything is neutral again." });
      },
    });

  if (state === null)
    return (
      <SettingsCard title="Business">
        <Loading fill />
      </SettingsCard>
    );
  if (state === false)
    return (
      <SettingsCard title="Business">
        <p className="lsf-dataset__none">The switch could not be read.</p>
      </SettingsCard>
    );
  return (
    <SettingsCard
      title="Business"
      description="Off, organisations and the docs see nothing about money and the neutral type names, and every organisation is served as the top type. On, the service sells by type: prices, subscriptions, invoices, compute prices, allowances and the business names show to organisations, each served as its own type. The platform's own pages always show the business names and the money."
    >
      <div className="lsf-dataset-form" data-testid="business-switch">
        <Toggle
          checked={state.on}
          disabled={!canEdit || busy}
          onChange={(e) => flip(e.target.checked)}
          label={state.on ? "The business is on" : "Everything is neutral"}
        />
        <p className="lsf-dataset-form__note">
          {state.switched_at ? `Switched ${when(state.switched_at)} by ${state.switched_by}. ` : "Never switched. "}
          Every organisation is served as at least {state.floor === "community" ? "its own type" : "the top type"} right
          now. After switching, publish the docs: the money pages follow this switch.
        </p>
        <span className="lsf-training__share-label">Names of the types once the business is on</span>
        <p className="lsf-dataset-form__note">
          The platform's own pages always use these. Organisations see them only when the business is on; off, they see
          the neutral names under Organization tiers. Prices are set under Platform, Subscriptions and show to
          organisations only when the business is on.
        </p>
        <div className="lsf-training__pcols">
          {Object.entries(names ?? state.names).map(([key, value]) => (
            <label key={key}>
              <span>{key}</span>
              <input
                type="text"
                value={value}
                maxLength={40}
                disabled={!canEdit || busy}
                onChange={(e) => setNames({ ...(names ?? state.names), [key]: e.target.value })}
              />
            </label>
          ))}
        </div>
        <div className="lsf-dataset-form__actions">
          <Button type="button" size="small" disabled={!canEdit || busy || !names} onClick={saveNames}>
            Save names
          </Button>
        </div>
      </div>
    </SettingsCard>
  );
};

export const platformSettingsBusinessTab = { key: "Business", component: BusinessTab, standalone: true };
